<script setup lang="ts">
import { inject, onBeforeUnmount, ref, watch } from 'vue';

import { imagePreviewHostKey } from './image-preview-host';

const props = defineProps<{ file: File; message?: boolean }>();
const emit = defineEmits<{ error: [] }>();
const url = ref('');
const dialog = ref<HTMLDialogElement | null>(null);
const previewHost = inject(imagePreviewHostKey, null);
let floated = false;

watch(() => props.file, (file, _previous, onCleanup) => {
  dialog.value?.close();
  if (floated) previewHost?.close();
  floated = false;
  const objectUrl = URL.createObjectURL(file);
  url.value = objectUrl;
  onCleanup(() => URL.revokeObjectURL(objectUrl));
}, { immediate: true });

/** 嵌入悬浮窗时请外层页面浮出大图；外层不接就仍在当前窗口里看。 */
async function openLarge(): Promise<void> {
  floated = false;
  if (previewHost && await previewHost.open(props.file)) {
    floated = true;
    return;
  }
  dialog.value?.showModal();
}

onBeforeUnmount(() => {
  dialog.value?.close();
  if (floated) previewHost?.close();
});
</script>

<template>
  <button
    class="image-attachment-thumbnail"
    :class="{ 'image-attachment-thumbnail--message': message }"
    type="button"
    :aria-label="`查看图片：${file.name}`"
    title="点击查看大图"
    data-testid="attachment-thumbnail"
    @click.stop="openLarge"
  >
    <img :src="url" :alt="file.name" @error="emit('error')" />
  </button>
  <Teleport to="body">
    <dialog ref="dialog" class="image-attachment-dialog" aria-label="图片预览" @click.self="dialog?.close()">
      <button class="image-attachment-close" type="button" aria-label="关闭图片预览" autofocus @click="dialog?.close()">关闭 ×</button>
      <img :src="url" :alt="file.name" />
      <p>{{ file.name }}</p>
    </dialog>
  </Teleport>
</template>

<style scoped>
.image-attachment-thumbnail {
  display: block;
  flex: 0 0 auto;
  width: 64px;
  height: 56px;
  padding: 0;
  overflow: hidden;
  border: 1px solid #dde2e8;
  border-radius: 6px;
  background: #fff;
  cursor: zoom-in;
}
.image-attachment-thumbnail img { display: block; width: 100%; height: 100%; object-fit: contain; }
.image-attachment-thumbnail--message { width: 240px; max-width: 100%; height: 160px; border-radius: 8px; }
.image-attachment-thumbnail:focus-visible { outline: 2px solid #0b7182; outline-offset: 2px; }
.image-attachment-dialog {
  position: fixed;
  inset: 0;
  margin: auto;
  width: fit-content;
  max-width: 94vw;
  max-height: 94vh;
  padding: 44px 12px 12px;
  overflow: auto;
  border: 0;
  border-radius: 12px;
  background: #fff;
  color: #303846;
  box-shadow: 0 16px 64px #0004;
}
.image-attachment-dialog::backdrop { background: #111827b3; }
.image-attachment-dialog > img { display: block; max-width: 86vw; max-height: 72vh; object-fit: contain; margin: auto; }
.image-attachment-dialog > p { margin: 8px 0 0; font-size: 12px; text-align: center; overflow-wrap: anywhere; }
.image-attachment-close { position: absolute; right: 12px; top: 8px; padding: 4px 8px; border: 0; border-radius: 4px; background: #f3f5f8; color: #303846; cursor: pointer; }
</style>
